import { ChangeEvent, useState } from "react";
import { Box, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { useFormikContext } from "formik";
import DownloadIcon from "@mui/icons-material/Download";

import { useAuth } from "../context/AuthContext";
import downloadCaCertificate from "../utils/downloadCaCertificate";

type SecurityFormValues = {
  accessTokenTtlMinutes: number;
  caCertPath: string;
};

const SecurityForm = () => {
  const { values, errors, touched, setFieldValue, handleBlur, handleChange } = useFormikContext<SecurityFormValues>();
  const { authorizedFetch } = useAuth();
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const handleAccessTokenTTLChange = (event: ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.valueAsNumber;
    if (Number.isNaN(rawValue)) {
      void setFieldValue("accessTokenTtlMinutes", Number.NaN);
      return;
    }

    void setFieldValue("accessTokenTtlMinutes", Math.round(rawValue));
  };

  const handleDownload = async () => {
    if (!values.caCertPath.trim()) {
      setDownloadError("Configure a certificate path before downloading.");
      return;
    }

    setDownloadError(null);
    setDownloading(true);
    try {
      await downloadCaCertificate(authorizedFetch);
    } catch (error) {
      setDownloadError(error instanceof Error ? error.message : "Unable to download certificate");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Stack spacing={2} sx={{ maxWidth: 320 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Tooltip title="Relative path to the trusted CA certificate used for outbound TLS verification.">
          <TextField
            label="CA certificate path"
            name="caCertPath"
            value={values.caCertPath}
            onChange={handleChange}
            onBlur={handleBlur}
            size="small"
            error={Boolean(touched.caCertPath && errors.caCertPath)}
            helperText={touched.caCertPath && errors.caCertPath}
            sx={{ flexGrow: 1 }}
          />
        </Tooltip>
        <Tooltip title="Download certificate">
          <span>
            <IconButton
              aria-label="Download certificate"
              onClick={() => { void handleDownload(); }}
              disabled={downloading}
              size="small"
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      {downloadError && (
        <Typography variant="body2" color="error">
          {downloadError}
        </Typography>
      )}
      <Tooltip title="Controls how long issued access tokens remain valid.">
        <TextField
          label="Access token TTL"
          name="accessTokenTtlMinutes"
          type="number"
          value={Number.isNaN(values.accessTokenTtlMinutes) ? "" : values.accessTokenTtlMinutes}
          onChange={handleAccessTokenTTLChange}
          onBlur={handleBlur}
          size="small"
          slotProps={{ htmlInput: { min: 10, max: 1200, step: 1 } }}
          error={Boolean(touched.accessTokenTtlMinutes && errors.accessTokenTtlMinutes)}
          helperText={touched.accessTokenTtlMinutes && errors.accessTokenTtlMinutes}
        />
      </Tooltip>
    </Stack>
  );
};

export default SecurityForm;
